import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import {
  type PlaceholderDefinition,
  type PlaceholderGroup,
} from '@/lib/patientReports/placeholders';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

const GROUPS: readonly PlaceholderGroup[] = ['report', 'patient', 'content'];

export function PlaceholderCopyList({
  items,
  layout = 'rows',
}: {
  items: readonly PlaceholderDefinition[];
  /** Rows match the settings catalog. Stack fits the narrow Mine tekster panel. */
  layout?: 'rows' | 'stack';
}) {
  const { t } = useTranslation();
  const copy = (token: string) => {
    void navigator.clipboard
      .writeText(token)
      .then(() => toast.success(t('patientReports.placeholders_copied')))
      .catch(() => toast.error(t('patientReports.placeholders_copy_error')));
  };

  return (
    <>
      {GROUPS.map((group) => {
        const groupItems = items.filter((item) => item.group === group);
        if (groupItems.length === 0) return null;
        return (
          <section key={group} className="flex flex-col gap-s2">
            <h3 className={cn(TYPE.body, 'font-medium text-ds-text')}>
              {t(`patientReports.placeholders_group_${group}`)}
            </h3>
            <ul className="divide-y divide-ds-line rounded-ctl border border-ds-line">
              {groupItems.map((item) => (
                <li
                  key={item.token}
                  className={
                    layout === 'stack'
                      ? 'flex flex-col gap-s2 px-s3 py-s3'
                      : 'flex flex-wrap items-center gap-s3 px-s4 py-s3'
                  }
                >
                  {layout === 'stack' ? (
                    <div className="flex items-start justify-between gap-s2">
                      <code className={cn(TYPE.body, 'font-medium text-ds-text')}>
                        {item.token}
                      </code>
                      <CopyButton token={item.token} onCopy={copy} />
                    </div>
                  ) : (
                    <code className={cn(TYPE.body, 'font-medium text-ds-text')}>{item.token}</code>
                  )}
                  <p
                    className={cn(
                      TYPE.small,
                      'text-ds-muted',
                      layout === 'rows' && 'min-w-[12rem] flex-1',
                    )}
                  >
                    {t(item.descriptionKey)}
                  </p>
                  {layout === 'rows' ? <CopyButton token={item.token} onCopy={copy} /> : null}
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </>
  );
}

function CopyButton({ token, onCopy }: { token: string; onCopy: (token: string) => void }) {
  const { t } = useTranslation();
  return (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      className="shrink-0"
      aria-label={`${t('patientReports.placeholders_copy')} ${token}`}
      onClick={() => onCopy(token)}
    >
      {t('patientReports.placeholders_copy')}
    </Button>
  );
}
